import { Navigate, Route, Routes } from 'react-router-dom';
import { CallDetailScreen } from './calls/CallDetailScreen';
import { CallPaymentScreen } from './calls/CallPaymentScreen';
import { CallsScreen } from './calls/CallsScreen';
import { DispatchChatScreen } from './dispatchChat/DispatchChatScreen';
import { DriverDetailScreen } from './drivers/DriverDetailScreen';
import { DriversScreen } from './drivers/DriversScreen';
import { FunctionsScreen } from './functions/FunctionsScreen';
import { HomeScreen } from './home/HomeScreen';
import { ImpoundDetailScreen } from './impounds/ImpoundDetailScreen';
import { ImpoundsScreen } from './impounds/ImpoundsScreen';
import { InspectionDetailScreen } from './inspections/InspectionDetailScreen';
import { InspectionRunScreen } from './inspections/InspectionRunScreen';
import { InspectionsScreen } from './inspections/InspectionsScreen';
import { InvoiceDetailScreen } from './invoices/InvoiceDetailScreen';
import { InvoicesScreen } from './invoices/InvoicesScreen';
import { OperationsScreen } from './operations/OperationsScreen';
import { SettingsScreen } from './settings/SettingsScreen';
import { SetupScreen } from './setup/SetupScreen';
import { MobileShell } from './shell/MobileShell';
import { RequireTab } from './shell/RequireTab';
import { MobileTheme } from '@relay-towing/ui';

/**
 * The new mobile app's screens, each at its own path, so a push's
 * `data.url`, a shared link and the back gesture all reach them. The paths
 * are the contract with the backend's push payloads and the wrappers
 * (docs/native-bridge.md): change one and change those.
 */
export function MobileApp() {
  return (
    <Routes>
      <Route
        element={
          <MobileTheme>
            <MobileShell />
          </MobileTheme>
        }
      >
        <Route index element={<HomeScreen />} />
        {/* A task opens in Home's task section. */}
        <Route path="tasks/:id" element={<HomeScreen />} />
        <Route
          path="calls"
          element={
            <RequireTab tab="calls">
              <CallsScreen />
            </RequireTab>
          }
        />
        <Route path="calls/:id" element={<CallDetailScreen />} />
        {/* Any member could ask the server for a call's payment; it
            refuses taking one without the key, and the screen says so. */}
        <Route path="calls/:id/payment" element={<CallPaymentScreen />} />
        <Route
          path="functions"
          element={
            <RequireTab tab="functions">
              <FunctionsScreen />
            </RequireTab>
          }
        />
        <Route
          path="operations"
          element={
            <RequireTab tab="operations">
              <OperationsScreen />
            </RequireTab>
          }
        />
        {/* Functions items: lists opened from that tab, which stays lit, or
            each a tab of its own for someone who only drives. */}
        <Route
          path="impounds"
          element={
            <RequireTab tab="functions" item="impounds">
              <ImpoundsScreen />
            </RequireTab>
          }
        />
        <Route path="impounds/:id" element={<ImpoundDetailScreen />} />
        <Route
          path="invoices"
          element={
            <RequireTab tab="functions" item="invoices">
              <InvoicesScreen />
            </RequireTab>
          }
        />
        <Route path="invoices/:id" element={<InvoiceDetailScreen />} />
        <Route
          path="inspections"
          element={
            <RequireTab tab="functions" item="inspections">
              <InspectionsScreen />
            </RequireTab>
          }
        />
        {/* A checklist picked from Inspections' New. The server refuses a run
            without inspections.create, and the screen says so rather than
            offering one. */}
        <Route
          path="inspections/run/:templateId"
          element={<InspectionRunScreen />}
        />
        {/* Where a run lands once its report is filed. */}
        <Route
          path="inspections/report/:id"
          element={<InspectionDetailScreen />}
        />
        {/* Operations items: screens opened from that tab, which stays lit. */}
        <Route
          path="drivers"
          element={
            <RequireTab tab="operations" item="drivers">
              <DriversScreen />
            </RequireTab>
          }
        />
        {/* Any member could ask the server for a driver; the screen is the
            office's, so it is held to the drivers item as the list is. */}
        <Route
          path="drivers/:id"
          element={
            <RequireTab tab="operations" item="drivers">
              <DriverDetailScreen />
            </RequireTab>
          }
        />
        {/* The dispatch chat, opened from Functions; it sends a member
            without calls.create home itself. */}
        <Route path="dispatch-chat" element={<DispatchChatScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        {/* A new owner's setup checklist; the shell opens it once. */}
        <Route path="onboarding" element={<SetupScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
